/** Live cloud tip notifications for the hosted web editor. */

import { Socket, type Channel } from "phoenix";
import {
  cloudEditorApiOrigin,
  resolvedCloudProjectId,
} from "./cloudEditorHost";
import { isJust, isNothing, just, nothing } from "./maybe";
import { trimmedOrEmpty, trimmedOrNull } from "./trimmedString";

export type StopWatchHostedEditorTips = () => void;

type ChannelTipPayload = {
  id?: string | null;
  git_head_oid?: string | null;
  gitHeadOid?: string | null;
  source?: string | null;
  updated_ref?: string | null;
  updatedRef?: string | null;
};

export type WatchHostedEditorTipsOptions = {
  projectId: string;
  /** Return true to defer reload (e.g. while saving or dirty). */
  shouldDefer?: () => boolean;
  onSynced: () => void | Promise<void>;
};

function hostedSocketUrl(): string {
  const parsed = new URL(cloudEditorApiOrigin());
  parsed.protocol = parsed.protocol === "https:" ? "wss:" : "ws:";
  parsed.pathname = "/socket";
  parsed.search = "";
  parsed.hash = "";
  return parsed.toString();
}

function tipSource(payload?: ChannelTipPayload | null): string {
  return (payload?.source ?? "").trim().toLowerCase();
}

/** Non-main consolidations (chronicle, share branch) must not be treated as
 * this tab's own web echo — same main tip, different ref (#105 polish). */
function isNonMainRefUpdate(payload?: ChannelTipPayload | null): boolean {
  const ref = (payload?.updated_ref ?? payload?.updatedRef ?? "").trim();
  return ref !== null && ref.length > 0 && ref !== "refs/heads/main";
}

async function fetchChannelToken(): Promise<string> {
  const res = await fetch(`${cloudEditorApiOrigin()}/v1/auth/channel-token`, {
    credentials: "include",
  });
  if (res.ok === false) {
    const body = await res.json().catch(() => ({}));
    const err =
      typeof body === "object" &&
      body !== null &&
      "error" in body &&
      typeof (body as { error?: unknown }).error === "string"
        ? trimmedOrNull((body as { error: string }).error)
        : null;
    throw new Error(err ?? `channel token failed (${res.status})`);
  }
  const body = (await res.json()) as { access_token?: string };
  const token = trimmedOrEmpty(body.access_token);
  if (token.length === 0)
    throw new Error("channel token missing from response");
  return token;
}

async function syncHostedSession(projectId: string): Promise<void> {
  const res = await fetch(
    `${cloudEditorApiOrigin()}/v1/projects/${projectId}/session/sync`,
    {
      method: "POST",
      credentials: "include",
    },
  );
  if (res.ok === false) {
    const body = await res.json().catch(() => ({}));
    const err =
      typeof body === "object" &&
      body !== null &&
      "error" in body &&
      typeof (body as { error?: unknown }).error === "string"
        ? trimmedOrNull((body as { error: string }).error)
        : null;
    throw new Error(err ?? `session sync failed (${res.status})`);
  }
}

/**
 * Subscribe to `snapshot_created` on the cloud project channel and reload the
 * editor when desktop (or another client) pushes a new backup.
 */
export function watchHostedEditorTips(
  opts: WatchHostedEditorTipsOptions,
): StopWatchHostedEditorTips {
  let socket: Socket | null = null;
  let channel: Channel | null = null;
  let stopped = false;
  let pending = false;
  let accessToken = "";

  const leaveChannel = (): void => {
    if (channel !== null) {
      channel.leave();
      channel = null;
    }
  };

  const runSync = async (): Promise<void> => {
    if (stopped) return;
    pending = false;
    try {
      await syncHostedSession(opts.projectId);
      await opts.onSynced();
    } catch (error) {
      console.warn("[cloudHostedRealtime] sync failed", error);
    }
  };

  const requestSync = (payload?: ChannelTipPayload | null): void => {
    if (stopped) return;
    // Skip this tab's own manuscript pushback echo; still accept chronicle /
    // share-branch updates from any source (including other web sessions).
    if (tipSource(payload) === "web" && isNonMainRefUpdate(payload) === false)
      return;

    if (opts.shouldDefer?.() === true) {
      pending = true;
      return;
    }

    void runSync();
  };

  const joinChannel = (): void => {
    if (
      socket === null ||
      stopped ||
      accessToken === null ||
      accessToken.length === 0
    )
      return;
    leaveChannel();

    channel = socket.channel(`project:${opts.projectId}`, {});
    channel
      .join()
      .receive("ok", (reply: ChannelTipPayload) => {
        console.info(
          "[cloudHostedRealtime] joined",
          `project:${opts.projectId}`,
        );
        requestSync(reply);
      })
      .receive("error", (reason) => {
        console.warn("[cloudHostedRealtime] join failed", reason);
        leaveChannel();
      })
      .receive("timeout", () => {
        console.warn("[cloudHostedRealtime] join timed out");
      });

    channel.on("snapshot_created", (payload: ChannelTipPayload) => {
      requestSync(payload);
    });
  };

  const connectSocket = (): void => {
    if (stopped || accessToken === null || accessToken.length === 0) return;

    if (socket === null) {
      socket = new Socket(hostedSocketUrl(), {
        params: () => ({ token: accessToken }),
      });
      socket.onOpen(() => {
        if (stopped) return;
        console.info("[cloudHostedRealtime] connected", hostedSocketUrl());
        joinChannel();
      });
      socket.onError(() => {
        if (stopped === false)
          console.warn("[cloudHostedRealtime] socket error");
      });
      socket.onClose(() => {
        if (stopped === false)
          console.warn("[cloudHostedRealtime] socket closed");
      });
    }

    socket.connect();
    if (socket.isConnected()) joinChannel();
  };

  void (async () => {
    try {
      accessToken = await fetchChannelToken();
      if (stopped) return;
      connectSocket();
    } catch (error) {
      console.warn("[cloudHostedRealtime] could not start", error);
    }
  })();

  const deferPoll = window.setInterval(() => {
    if (pending === false || stopped || opts.shouldDefer?.() === true) return;
    void runSync();
  }, 1500);

  return () => {
    stopped = true;
    pending = false;
    window.clearInterval(deferPoll);
    leaveChannel();
    socket?.disconnect();
    socket = null;
  };
}

/** Convenience wrapper when only `rootPath` is known at the call site. */
export function watchHostedEditorTipsForRoot(
  rootPath: string | null,
  opts: Omit<WatchHostedEditorTipsOptions, "projectId">,
): StopWatchHostedEditorTips {
  const projectId = resolvedCloudProjectId(
    rootPath !== null ? just(rootPath) : nothing,
  );
  if (
    isNothing(projectId) ||
    (isJust(projectId) && projectId.value.length === 0)
  ) {
    console.warn(
      "[cloudHostedRealtime] no cloud project id; skip channel watch",
    );
    return () => {};
  }
  return watchHostedEditorTips({ ...opts, projectId: projectId.value });
}
